import { flow, isEqual, set, isNull } from 'lodash/fp';
import { Reducer } from 'redux';

import { LoginActionTypes } from '../../../auth/login/store/types';
import { PreExecutionFilterOption } from '../../search/store/types';
import { AgreementTypeReportFields, DocumentReportTemplate, DocumentReportingActionTypes, DocumentReportingState, ReportPage, ReportTab } from './types';

export const initialDocumentReportFilter = {
    documentNameId: null,
    entityId: null,
    includePreExecution: PreExecutionFilterOption.EXCLUDE,
    date: {
        to: null,
        from: null
    },
    isExclusiveEntityFilter: false
};

const getSelectedReportFields = (allReportFields: AgreementTypeReportFields, selectedAgreementTypeId: number | null) => !isNull(selectedAgreementTypeId) && allReportFields[`${selectedAgreementTypeId}`] || [];

export const INITIAL_STATE: DocumentReportingState = {
    reportPage: ReportPage.SELECT,
    selectedTab: ReportTab.SELECT,
    selectedAgreementTypeId: null,
    reportFields: {},
    fetchingAvailableFields: false,
    generatingReport: false,
    fetchingError: null,
    reportFilters: initialDocumentReportFilter,
    reportUpdated: false,
    backConfirmationModalOpen: false,
    reportTemplates: [],
    saveTemplateModalOpen: false,
    templateDescription: '',
    templateName: '',
    saveTemplateError: null,
    selectedTemplate: null,
    isDeleting: false,
    confirmDeleteReportTemplate: null,
    fetchingTemplates: false,
    definitionHierarchy: [],
    openFieldsAndSections: [],
    clientHiddenFields: {},
    documentHiddenFields: {},
    searchFieldSection: null,
    searchFuzzyMatches: [],
    fieldSectionSearch: '',
    allSearchFieldSections: [],
    metaFields: []
};

export const documentReportingReducer: Reducer<DocumentReportingState> = (state = INITIAL_STATE, action): DocumentReportingState => {
    switch (action.type) {
        case DocumentReportingActionTypes.SELECT_REPORT_PAGE:
            return set('reportPage', action.payload.page, state);
        case DocumentReportingActionTypes.SELECT_WIZARD_TAB:
            return set('selectedTab', action.payload, state);
        case DocumentReportingActionTypes.UPDATE_REPORT_SELECTED_AGREEMENT_TYPE:
            return set('selectedAgreementTypeId', action.payload, state);
        case DocumentReportingActionTypes.UPDATE_DOCUMENT_REPORT_FILTER: {
            const { key, value } = action.payload;
            return set(`reportFilters[${key}]`, value, state);
        }
        case DocumentReportingActionTypes.UPDATE_REPORT_DATE_FILTER: {
            const { key, value } = action.payload;
            return set(`reportFilters.date[${key}]`, value, state);
        }
        case DocumentReportingActionTypes.SET_REPORT_AGREEMENT_TYPES: {
            const agreementTypeIds: string[] | null = action.payload;
            if (isNull(agreementTypeIds)) {
                return set('reportFields', {}, state);
            }
            const reportFields = agreementTypeIds.reduce((acc: AgreementTypeReportFields, selectedAgreementTypeId) => {
                const currentAgreementTypeReportFields = state.reportFields[selectedAgreementTypeId] || [];
                return set(selectedAgreementTypeId, currentAgreementTypeReportFields, acc);
            }, {});
            return set('reportFields', reportFields, state);
        }
        case DocumentReportingActionTypes.FETCH_AVAILABLE_FIELDS_STARTED:
            return flow(
                set('fetchingAvailableFields', true),
                set('definitionHierarchy', []),
                set('clientHiddenFields', {}),
                set('documentHiddenFields', {}),
                set('fetchingError', null),
                set('allSearchFieldSections', []),
                set('searchFieldSection', null),
                set('fieldSectionSearch', ''),
                set('searchFuzzyMatches', [])
            )(state);
        case DocumentReportingActionTypes.FETCH_AVAILABLE_DOCUMENT_FIELDS_SUCCESSFUL: {
            const { definitionHierarchy, clientHiddenFields, documentHiddenFields } = action.payload;
            return flow(
                set('fetchingAvailableFields', false),
                set('definitionHierarchy', definitionHierarchy),
                set('clientHiddenFields', clientHiddenFields),
                set('documentHiddenFields', documentHiddenFields)
            )(state);
        }
        case DocumentReportingActionTypes.FETCH_AVAILABLE_FIELDS_FAILED:
            return flow(
                set('fetchingAvailableFields', false),
                set('fetchingError', action.payload)
            )(state);
        case DocumentReportingActionTypes.ADD_DOCUMENT_REPORT_FIELD: {
            return set(`reportFields[${state.selectedAgreementTypeId}]`, [...getSelectedReportFields(state.reportFields, state.selectedAgreementTypeId), action.payload], state);
        }
        case DocumentReportingActionTypes.REMOVE_DOCUMENT_REPORT_FIELD:
            return set(`reportFields[${state.selectedAgreementTypeId}]`, getSelectedReportFields(state.reportFields, state.selectedAgreementTypeId).filter(({ id }) => id !== action.payload), state);
        case DocumentReportingActionTypes.UPDATE_DOCUMENT_REPORT_FIELDS:
            return set(`reportFields[${state.selectedAgreementTypeId}]`, action.payload, state);
        case DocumentReportingActionTypes.UPDATE_INSTANCE_REPORT_FIELD_CUSTOM_LABEL: {
            const { id, value } = action.payload;
            const updatedReportFields = getSelectedReportFields(state.reportFields, state.selectedAgreementTypeId).map(reportField => {
                if (`${reportField.parentFieldId}-${reportField.id}` === id) {
                    return set('customLabel', value, reportField);
                }
                return reportField;
            });
            return set(`reportFields[${state.selectedAgreementTypeId}]`, updatedReportFields, state);
        }
        case DocumentReportingActionTypes.UPDATE_INSTANCE_REPORT_FIELD_INCLUDE_DATASET_TITLE: {
            const { id, value } = action.payload;
            const updatedReportFields = getSelectedReportFields(state.reportFields, state.selectedAgreementTypeId).map(reportField => {
                if (`${reportField.parentFieldId}-${reportField.id}` === id) {
                    return set('includeDatasetTitle', value, reportField);
                }
                return reportField;
            });
            return set(`reportFields[${state.selectedAgreementTypeId}]`, updatedReportFields, state);
        }
        case DocumentReportingActionTypes.GENERATE_REPORT_STARTED:
            return set('generatingReport', true, state);
        case DocumentReportingActionTypes.GENERATE_REPORT_FAILED:
            return set('generatingReport', false, state);
        case DocumentReportingActionTypes.GENERATE_REPORT_SUCCESSFUL:
            return flow(
                set('reportPage', ReportPage.SELECT),
                set('selectedTab', ReportTab.SELECT),
                set('generatingReport', false),
                set('reportFilters', initialDocumentReportFilter),
                set('reportFields', {}),
                set('reportUpdated', false),
                set('selectedAgreementTypeId', null)
            )(state);
        case DocumentReportingActionTypes.RESET_REPORT: {
            return { ...INITIAL_STATE, reportTemplates: state.reportTemplates, reportPage: action.payload };
        }
        case DocumentReportingActionTypes.TOGGLE_REPORT_CONFIRMATION_MODAL:
            return set('backConfirmationModalOpen', action.payload, state);
        case DocumentReportingActionTypes.SET_REPORT_UPDATED:
            return set('reportUpdated', action.payload, state);
        case DocumentReportingActionTypes.TOGGLE_SAVE_TEMPLATE_MODAL_OPEN:
            return flow(
                set('saveTemplateModalOpen', action.payload),
                set('templateName', ''),
                set('templateDescription', '')
            )(state);
        case DocumentReportingActionTypes.UPDATE_REPORT_TEMPLATE_VALUE: {
            const { key, value } = action.payload;
            return set(`${key}`, value, state);
        }
        case DocumentReportingActionTypes.SAVE_REPORT_TEMPLATE_FAILED:
            return set('saveTemplateError', action.payload, state);
        case DocumentReportingActionTypes.SAVE_REPORT_TEMPLATE_SUCCESSFUL:
            return flow(
                set('saveTemplateError', null),
                set('saveTemplateModalOpen', false),
                set('reportUpdated', false),
                set('selectedTemplate', action.payload)
            )(state);
        case DocumentReportingActionTypes.FETCH_ALL_REPORT_TEMPLATES_STARTED:
            return set('fetchingTemplates', true, state);
        case DocumentReportingActionTypes.FETCH_ALL_REPORT_TEMPLATES_SUCCESSFUL:
            return flow(
                set('reportTemplates', action.payload),
                set('fetchingTemplates', false)
            )(state);
        case DocumentReportingActionTypes.FETCH_ALL_REPORT_TEMPLATES_FAILED:
            return flow(
                set('fetchingError', action.payload),
                set('fetchingTemplates', false)
            )(state);
        case DocumentReportingActionTypes.OPEN_DOCUMENT_REPORT_TEMPLATE: {
            const reportTemplate: DocumentReportTemplate = action.payload;
            return flow(
                set('selectedTemplate', reportTemplate),
                set('reportPage', ReportPage.WIZARD),
                set('selectedTab', ReportTab.SELECT),
                set('reportFilters', reportTemplate.filters),
                set('metaFields', reportTemplate.metaFields),
                set('reportFields', reportTemplate.reportFields),
                set('selectedAgreementTypeId', parseInt(Object.keys(reportTemplate.reportFields)[0]))
            )(state);
        }
        case DocumentReportingActionTypes.DELETE_REPORT_TEMPLATE_STARTED:
            return set('isDeleting', true, state);
        case DocumentReportingActionTypes.DELETE_REPORT_TEMPLATE_SUCCESSFUL:
            return flow(
                set('isDeleting', false),
                set('reportTemplates', action.payload),
                set('confirmDeleteReportTemplate', null)
            )(state);
        case DocumentReportingActionTypes.DELETE_REPORT_TEMPLATE_FAILED:
            return set('isDeleting', false, state);
        case DocumentReportingActionTypes.SET_REPORT_TEMPLATE_TO_DELETE:
            return set('confirmDeleteReportTemplate', action.payload, state);
        case DocumentReportingActionTypes.REMOVE_REPORT_DATASETS_OPEN_FIELD_SECTION: {
            const openFieldsAndSections = state.openFieldsAndSections.filter(fieldSection => !isEqual(fieldSection, action.payload));
            return set('openFieldsAndSections', openFieldsAndSections, state);
        }
        case DocumentReportingActionTypes.ADD_REPORT_DATASETS_OPEN_FIELD_SECTION: {
            const openFieldsAndSections = [...state.openFieldsAndSections, action.payload];
            return set('openFieldsAndSections', openFieldsAndSections, state);
        }
        case DocumentReportingActionTypes.REMOVE_ALL_REPORT_DATASETS_OPEN_FIELD_SECTIONS:
            return set('openFieldsAndSections', INITIAL_STATE.openFieldsAndSections, state);
        case DocumentReportingActionTypes.EXPAND_ALL_REPORT_DATASETS_OPEN_FIELD_SECTIONS:
            return set('openFieldsAndSections', action.payload, state);
        case DocumentReportingActionTypes.SET_REPORT_SEARCH_FIELD_SECTION:
            return set('searchFieldSection', action.payload, state);
        case DocumentReportingActionTypes.SET_REPORT_DATASETS_SEARCH_FUZZY_MATCHES:
            return set('searchFuzzyMatches', action.payload, state);
        case DocumentReportingActionTypes.SET_REPORT_DATASETS_FUZZY_MATCH_SEARCH_VALUE:
            return set('fieldSectionSearch', action.payload, state);
        case DocumentReportingActionTypes.SET_ALL_DATASET_REPORT_SEARCH_FIELD_SECTIONS:
            return set('allSearchFieldSections', action.payload, state);
        case DocumentReportingActionTypes.RESET_REPORT_SEARCH_FIELD_SECTIONS:
            return flow(
                set('allSearchFieldSections', []),
                set('searchFieldSection', null),
                set('fieldSectionSearch', ''),
                set('searchFuzzyMatches', [])
            )(state);
        case DocumentReportingActionTypes.SET_DOCUMENT_REPORT_META_FIELDS:
            return set('metaFields', action.payload, state);
        case LoginActionTypes.LOGOUT_SUCCESSFUL:
            return INITIAL_STATE;
        default:
            return state;
    }
};
